import { Button, HStack, Image, Select, Spacer, Text, VStack, useToast } from '@chakra-ui/react';
import { useCreateBannerMutation } from '@partiu/api/dashboard/hooks.ts';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { FaPlus } from 'react-icons/fa6';
import { useState } from 'react';
import { Input } from '@partiu/ui/components/atoms/Input.tsx';
import { AddModalProps } from '../../types';
import { ICreateBannerData } from '@partiu/api/dashboard/types.ts';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB em bytes

export function BannerForm({ onClose = () => {}, options }: AddModalProps<ICreateBannerData>) {
  const [name, setName] = useState<string>('');
  const [desktopImage, setDesktopImage] = useState<File | null>(null);
  const [desktopPreviewUrl, setDesktopPreviewUrl] = useState<string | ArrayBuffer | null>(null);
  const [mobileImage, setMobileImage] = useState<File | null>(null);
  const [mobilePreviewUrl, setMobilePreviewUrl] = useState<string | ArrayBuffer | null>(null);
  const mutation = useCreateBannerMutation(options);
  const toast = useToast();

  const [url, setUrl] = useState<string>('');
  const [type, setType] = useState<'discount' | 'book'>('discount');

  const handleFileLoad = (
    file: File,
    setImage: (file: File | null) => void,
    setPreviewUrl: (url: string | ArrayBuffer | null) => void
  ) => {
    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: 'Arquivo muito grande',
        description: `O arquivo deve ter no máximo 2MB. Tamanho atual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setImage(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setPreviewUrl(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const loadFileWithValidation = (
    accept: string,
    setImage: (file: File | null) => void,
    setPreviewUrl: (url: string | ArrayBuffer | null) => void
  ) => {
    const element = document.createElement('input');
    element.type = 'file';
    element.accept = accept;
    document.body.append(element);

    element.onchange = function (event) {
      const target = event.target as HTMLInputElement;
      if (!target || !target.files) return;

      const file = target.files[0];
      handleFileLoad(file, setImage, setPreviewUrl);
      element.remove();
    };

    element.click();
  };

  return (
    <VStack spacing={7} align={'flex-start'}>
      <Labeled label={'Nome do Banner'}>
        <Input value={name} onValueChange={setName} />
      </Labeled>
      <Labeled label={'Tipo do Banner'}>
        <Select value={type} onChange={(event) => setType(event.target.value as 'discount' | 'book')}>
          <option value="discount">Desconto</option>
          <option value="book">Livro</option>
        </Select>
      </Labeled>
      <Labeled label={'Imagem Desktop'}>
        <Text>A imagem deve ser no tamanho 3000 × 696 px (pixels) ou proporcionais a isso em 4.31:1.</Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Tamanho máximo: 2MB
        </Text>
        <Button
          width={'100%'}
          onClick={() => loadFileWithValidation('image/*', setDesktopImage, setDesktopPreviewUrl)}
          colorScheme="blue"
        >
          Escolher Imagem
        </Button>
        {desktopPreviewUrl && <Image src={desktopPreviewUrl as string} />}
      </Labeled>
      <Labeled label={'Imagem Mobile'}>
        <Text>A imagem deve ser no tamanho 1000 × 622 (pixels) ou proporcionais a isso em 1.61:1.</Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Tamanho máximo: 2MB
        </Text>
        <Button
          width={'100%'}
          onClick={() => loadFileWithValidation('image/*', setMobileImage, setMobilePreviewUrl)}
          colorScheme="blue"
        >
          Escolher Imagem
        </Button>
        {mobilePreviewUrl && <Image src={mobilePreviewUrl as string} />}
      </Labeled>
      <Labeled label={'Url Externa (Opcional)'}>
        <Input value={url} onValueChange={setUrl} />
      </Labeled>
      <HStack width={'100%'}>
        <Spacer />
        <Button isLoading={mutation.isLoading} variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          isDisabled={!name || !desktopImage || !mobileImage}
          leftIcon={<FaPlus />}
          isLoading={mutation.isLoading}
          onClick={() =>
            mutation.mutate({
              name,
              image: desktopImage,
              imageMobile: mobileImage,
              url: url !== '' ? url : null,
              type,
            })
          }
          variant={'solid'}
          colorScheme={'green'}
        >
          Criar
        </Button>
      </HStack>
    </VStack>
  );
}
